import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Typography } from '../Typography';
import { Button, ButtonColors, ButtonSizes } from '../Button';
import ErrorSvg from '#assets/images/error_illustration_2.svg';

import styles from './ErrorDisplay.module.css';

export type ErrorDisplayProps = {
  error?: Error;
  info?: {
    componentStack?: string;
  };
  reset?: () => void;
  onReload?: () => void;
  onLogout?: () => void;
  showToastInDev?: boolean;
  environment?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  logoutButtonText?: string;
};

export const ErrorDisplay = ({
  error,
  reset,
  onReload,
  onLogout,
  showToastInDev = true,
  environment = 'production',
  title = 'Something went wrong',
  description = "We're sorry, but something went wrong. Please try again later.",
  buttonText = 'Reload Page',
  logoutButtonText = 'Log Out',
}: ErrorDisplayProps) => {
  const reloadPage = () => {
    if (onReload) {
      onReload();
    } else if (reset) {
      reset();
    }
  };

  useEffect(() => {
    if (showToastInDev && environment === 'development' && error) {
      toast.error(error.message, {
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        style: {
          width: 'auto',
          height: 'auto',
          padding: '1rem',
          fontSize: '1rem',
          borderRadius: '0.5rem',
        },
      });
    }
  }, [error, showToastInDev, environment]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography variant='heading-xl' color='purple' className='text-center'>
          {title}
        </Typography>
        <div className={styles.illustration}>
          <img src={ErrorSvg} alt='Error Illustration' />
        </div>
        <Typography
          variant='body-s'
          color='purple'
          className='text-center mt-4'
        >
          {description}
        </Typography>
        <div className={styles.actions}>
          <Button
            onClick={reloadPage}
            color={ButtonColors.PURPLE}
            size={ButtonSizes.M}
          >
            {buttonText}
          </Button>
          {onLogout && (
            <Button
              onClick={onLogout}
              color={ButtonColors.PURPLE}
              size={ButtonSizes.M}
              variant='outline'
            >
              {logoutButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
